import { TableActionButton, TableActionGroup } from '@channelplay/table-actions';
// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import FormModal from '../components/FormModal';
import { FormErrorBanner } from '../components/FormErrorBanner';
import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CornerDownRight } from 'lucide-react';
import ActiveToggle from '../components/ActiveToggle';
import { productMasterAPI, type ProductGroup } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { Skeleton } from '../components/Skeleton';

/**
 * Master Data → Products → Groups.
 *
 * The filing tree, and ONLY the filing tree. Brand, Category, Sub-Category all
 * live here as nested groups of the same kind, so a project that wants three
 * levels or five gets them without a schema change.
 *
 * What deliberately does NOT live here is variation. Size, Colour and Storage are
 * characteristics of a product, not levels of a hierarchy — nesting them would
 * mean hand-creating every combination (8 sizes x 4 colours x 2 Bluetooth = 64
 * rows for one ring, and 16 more per new colour). Those live on the Products tab
 * and get multiplied out into SKUs.
 */

const apiMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

export default function ProductGroupsPage({
  embedded, actionSlot,
}: { embedded?: boolean; actionSlot?: HTMLElement | null } = {}) {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);
  const isSuperAdmin = useAuthStore((state) => state.user?.adminRole === 'super_admin');

  const [groups, setGroups] = useState<ProductGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<ProductGroup | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<string>('');

  const load = useCallback(async () => {
    if (!selectedProjectId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await productMasterAPI.listGroups(selectedProjectId, true);
      setGroups(res.data?.data || []);
    } catch (err) {
      setError(apiMessage(err, 'Failed to load folders.'));
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setParentId('');
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEdit = (group: ProductGroup) => {
    setEditing(group);
    setName(group.name);
    setParentId(group.parentId ?? '');
    setFormError(null);
    setIsModalOpen(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectId) return;
    setIsSubmitting(true);
    setFormError(null);
    try {
      const body = { name, parentId: parentId || null };
      if (editing) {
        await productMasterAPI.updateGroup(selectedProjectId, editing.id, body);
      } else {
        await productMasterAPI.createGroup(selectedProjectId, body);
      }
      setIsModalOpen(false);
      await load();
    } catch (err) {
      setFormError(apiMessage(err, 'Failed to save the folder.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Refused by the API while the folder still holds products or a task question
  // scopes to it — the products would otherwise survive un-filed, which reads as
  // data loss to whoever finds them.
  const removeGroup = async (group: ProductGroup) => {
    if (!selectedProjectId) return;
    if (!confirm(`Delete the folder "${group.name}" permanently?`)) return;
    try {
      await productMasterAPI.deleteGroup(selectedProjectId, group.id);
      await load();
    } catch (err) {
      // Only offer the override to a super admin — the API refuses it for anyone
      // else, and prompting for a destructive action that will be denied is a
      // worse experience than not offering it. The server check is the real gate;
      // this only keeps the prompt honest.
      const code = (err as any)?.response?.data?.code;
      if (isSuperAdmin && (code === 'FOLDER_NOT_EMPTY' || code === 'FOLDER_IN_USE')) {
        const reason = apiMessage(err, '');
        // Milder than the product case: products are un-filed, not deleted.
        if (confirm(`${reason}\n\nDelete the folder anyway? Its products are kept — they just stop being filed under it.`)) {
          try {
            await productMasterAPI.deleteGroup(selectedProjectId, group.id, true);
            await load();
            return;
          } catch (forceErr) {
            setError(apiMessage(forceErr, 'Failed to delete the folder.'));
            return;
          }
        }
        return;
      }
      setError(apiMessage(err, 'Failed to delete the folder.'));
    }
  };

  const toggleActive = async (group: ProductGroup) => {
    if (!selectedProjectId) return;
    try {
      await productMasterAPI.setGroupActive(selectedProjectId, group.id, !group.active);
      await load();
    } catch (err) {
      setError(apiMessage(err, 'Failed to update the folder.'));
    }
  };

  // A group cannot be re-parented under itself or its own descendants. The server
  // rejects it either way (a cycle would strand the branch — listGroups walks down
  // from the roots, so a detached ring is invisible while its products stay live),
  // but filtering here means the option is never offered in the first place.
  const parentOptions = groups.filter((g) => {
    if (!editing) return true;
    if (g.id === editing.id) return false;
    return !g.path.startsWith(`${editing.path} > `);
  });

  const addButton = (
    <button
      className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
      onClick={openCreate}
    >
      Add Folder
    </button>
  );

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Folders</h1>
        </div>
      )}

      {actionSlot ? createPortal(addButton, actionSlot) : (
        <div className="flex items-center justify-end gap-4">{addButton}</div>
      )}

      {error && <FormErrorBanner message={error} />}

      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <th className="whitespace-nowrap px-6 py-4">Folder</th>
                <th className="whitespace-nowrap px-6 py-4">Products</th>
                <th className="whitespace-nowrap px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-40" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-12" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-16" /></td>
                  </tr>
                ))
              ) : groups.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center text-sm text-gray-500">
                    No folders yet, and that is fine — folders are optional. Add one
                    only if you carry several brands and want them kept apart.
                  </td>
                </tr>
              ) : (
                groups.map((group) => (
                  <tr key={group.id} className={group.active ? 'hover:bg-gray-50' : 'bg-gray-50/60 text-gray-400'}>
                    <td className="whitespace-nowrap px-6 py-4 font-medium text-gray-900">
                      {/* Indent by depth so the tree reads as a tree in a flat table. */}
                      <span style={{ paddingLeft: `${group.depth * 20}px` }} className="inline-flex items-center gap-1.5">
                        {group.depth > 0 && <CornerDownRight className="h-3.5 w-3.5 text-gray-300" />}
                        {group.name}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-gray-600">{group.productCount ?? 0}</td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <TableActionGroup>
                        <TableActionButton action="edit" label={`Edit folder ${group.name}`}
                          type="button"
                          onClick={() => openEdit(group)}
                        />
                        {/* Retiring cascades to sub-groups: a visible child under a
                            hidden parent is unreachable in the tree, so it would look
                            deleted while still feeding pickers. */}
                        <ActiveToggle
                          active={group.active}
                          onToggle={() => toggleActive(group)}
                          label={group.name}
                          onLabel="Active — click to hide this folder and everything in it"
                          offLabel="Inactive — click to bring it back"
                        />
                        <TableActionButton action="delete" label={`Delete folder ${group.name}`}
                          type="button"
                          onClick={() => removeGroup(group)}
                          title="Delete permanently"
                        />
                      </TableActionGroup>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <FormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editing ? 'Edit Folder' : 'Add Folder'}
        size="sm"
        formId="product-group-form"
        isSubmitting={isSubmitting}
        submitLabel="Save"
      >
        <form id="product-group-form" onSubmit={submit} className="space-y-4">
          {formError && <FormErrorBanner message={formError} />}

          <div>
            <label htmlFor="group-name" className="mb-1 block text-sm font-medium text-gray-700">
              Folder Name
            </label>
            <input
              id="group-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Microsoft"
              maxLength={150}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label htmlFor="group-parent" className="mb-1 block text-sm font-medium text-gray-700">
              Inside
            </label>
            <select
              id="group-parent"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">— Top level —</option>
              {parentOptions.map((g) => (
                <option key={g.id} value={g.id}>{g.path}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Leave empty for a top-level folder.
            </p>
          </div>
        </form>
      </FormModal>
    </div>
  );
}
